import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useI18n } from "@/lib/i18n";

/**
 * EPC069-12 SEPA Credit Transfer QR code.
 * Scannable by all major EU banking apps (Bunq, ING, Rabobank, ABN AMRO,
 * Revolut, N26, Sparkasse, etc.) to pre-fill an outgoing SEPA payment.
 *
 * Spec fields (line-separated):
 *  1. Service Tag        BCD
 *  2. Version            002
 *  3. Character Set      1     (UTF-8)
 *  4. Identification     SCT
 *  5. BIC                (optional in v2)
 *  6. Beneficiary Name   (max 70)
 *  7. IBAN               (no spaces)
 *  8. Amount             EUR12.34
 *  9. Purpose            (empty or 4 char)
 * 10. Structured Ref     (empty)
 * 11. Unstructured Ref   (order reference)
 * 12. Beneficiary info   (empty)
 */
function buildEpcPayload({ bic, holder, iban, amount, reference }) {
  const ibanClean = String(iban || "").replace(/\s+/g, "").toUpperCase();
  const amt = Number(amount || 0);
  const amountStr = `EUR${amt.toFixed(2)}`;
  const lines = [
    "BCD",
    "002",
    "1",
    "SCT",
    (bic || "").trim(),
    (holder || "").slice(0, 70),
    ibanClean,
    amountStr,
    "",
    "",
    (reference || "").slice(0, 35),
    "",
  ];
  return lines.join("\n");
}

export default function BunqQrCode({ amount, reference, bank }) {
  const { lang } = useI18n();
  const [zoom, setZoom] = useState(false);

  if (!bank || !amount || !reference) return null;

  const payload = buildEpcPayload({
    bic: bank.bic,
    holder: bank.account_holder,
    iban: bank.iban,
    amount,
    reference,
  });

  const copy = {
    nl: {
      badge: "SCAN & BETAAL",
      title: "Scan met je bank app",
      sub: "Open Bunq, ING, Rabo of ABN → scan deze code. Bedrag, IBAN en kenmerk staan direct goed.",
      hint: "Werkt met elke Nederlandse & Europese bank app die SEPA-QR ondersteunt.",
      zoom: "VERGROOT",
      close: "SLUIT",
    },
    en: {
      badge: "SCAN & PAY",
      title: "Scan with your bank app",
      sub: "Open Bunq, ING, Rabo, Revolut, N26 or Sparkasse → scan this code. Amount, IBAN and reference will be pre-filled.",
      hint: "Works with any SEPA-QR compatible banking app in the EU.",
      zoom: "ZOOM",
      close: "CLOSE",
    },
    de: {
      badge: "SCANNEN & BEZAHLEN",
      title: "Mit Ihrer Banking-App scannen",
      sub: "Öffnen Sie Bunq, ING, Sparkasse oder N26 → Code scannen. Betrag, IBAN und Verwendungszweck werden automatisch ausgefüllt.",
      hint: "Funktioniert mit jeder SEPA-QR-kompatiblen Banking-App in der EU.",
      zoom: "VERGRÖSSERN",
      close: "SCHLIESSEN",
    },
  };
  const c = copy[lang] || copy.en;

  return (
    <>
      <div
        data-testid="bunq-qr-block"
        className="relative overflow-hidden border-2 border-black bg-white p-6 md:p-10 mb-8"
      >
        <div
          className="absolute -top-24 -right-24 w-[380px] h-[380px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(0,82,155,0.14), transparent 65%)",
            filter: "blur(50px)",
          }}
          aria-hidden
        />
        <div className="relative grid grid-cols-1 md:grid-cols-[auto,1fr] gap-8 items-center">
          <button
            type="button"
            onClick={() => setZoom(true)}
            className="group relative bg-white p-4 border border-neutral-300 mx-auto md:mx-0"
            data-testid="bunq-qr-code"
            aria-label={c.zoom}
          >
            <QRCodeSVG
              value={payload}
              size={200}
              level="M"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#111111"
            />
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition bg-black/60 text-white font-mono-label text-[10px] tracking-[0.2em]">
              {c.zoom} ⌕
            </span>
          </button>

          <div>
            <p className="font-mono-label text-[10px] text-[#00529B] mb-3">
              [ {c.badge} ]
            </p>
            <h3 className="font-serif-display text-3xl md:text-4xl leading-tight mb-3">
              {c.title}
            </h3>
            <p className="text-neutral-700 leading-relaxed">{c.sub}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="font-mono-label text-[10px] tracking-[0.2em] border border-black px-2.5 py-1">
                BUNQ
              </span>
              <span className="font-mono-label text-[10px] tracking-[0.2em] border border-neutral-300 px-2.5 py-1">
                ING
              </span>
              <span className="font-mono-label text-[10px] tracking-[0.2em] border border-neutral-300 px-2.5 py-1">
                RABO
              </span>
              <span className="font-mono-label text-[10px] tracking-[0.2em] border border-neutral-300 px-2.5 py-1">
                ABN
              </span>
              <span className="font-mono-label text-[10px] tracking-[0.2em] border border-neutral-300 px-2.5 py-1">
                REVOLUT
              </span>
              <span className="font-mono-label text-[10px] tracking-[0.2em] border border-neutral-300 px-2.5 py-1">
                N26
              </span>
            </div>
            <p className="mt-4 font-mono-label text-[10px] text-neutral-500">
              {c.hint}
            </p>
          </div>
        </div>
      </div>

      {zoom && (
        <div
          data-testid="bunq-qr-modal"
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-6"
          onClick={() => setZoom(false)}
        >
          <div
            className="bg-white p-8 max-w-md w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-mono-label text-[10px] text-[#00529B] mb-4">
              [ {c.badge} ]
            </p>
            <div className="flex justify-center">
              <QRCodeSVG
                value={payload}
                size={320}
                level="M"
                includeMargin={false}
                bgColor="#FFFFFF"
                fgColor="#111111"
              />
            </div>
            <button
              type="button"
              onClick={() => setZoom(false)}
              className="mt-6 btn-sharp"
              data-testid="bunq-qr-close"
            >
              {c.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
